import { ChangeEvent, Component, FocusEventHandler } from 'react';
import InputMask from 'react-input-mask';
import { connect } from 'react-redux';

import { OnChangeType, OnKeyPressType } from 'types/functions';
import { twclsx } from 'utils/twclsx';

interface InputProps {
    className?: string;
    value?: string | readonly string[] | number | undefined;
    name: string;
    type?: string;
    min?: string;
    language?: string;
    onChange?: OnChangeType;
    onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
    onKeyPress?: OnKeyPressType; // Added onKeyPress
    placeholder?: { en: string; ar: string } | string;
    disabled?: boolean;
    autoFocus?: boolean;
    maxLength?: number;
    isValid?: boolean;
    mask?: string;
    maskChar?: string;
}

interface InputState {
    language: string;
    value: string;
}

class Input extends Component<InputProps, InputState> {
    constructor(props: InputProps) {
        super(props);
        this.state = {
            language: 'en',
            value: '',
        };
    }

    static getDerivedStateFromProps(props: InputProps, state: InputState) {
        if (props.language !== state.language || props.value !== state.value) {
            return {
                language: props.language,
                value: props.value,
            };
        }
        return null;
    }

    handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (this.props.disabled || !this.props?.onChange) {
            return false;
        }

        const { value, name } = e.target;
        const { maxLength, onChange } = this.props;

        // If maxLength is provided and the input exceeds maxLength, truncate the value
        if (maxLength && value.length > maxLength) {
            onChange?.(name, value.slice(0, maxLength));
        } else {
            onChange?.(name, value);
        }
    };

    handlePress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.disabled || !this.props?.onKeyPress) {
            return false;
        }
        this.props.onKeyPress?.(this.props.name, e.key);
    };

    render() {
        const {
            name,
            value,
            language,
            placeholder,
            disabled,
            autoFocus,
            type,
            min,
            maskChar = ' ',
            mask,
            className,
            isValid = true,
        } = this.props;

        return (
            <>
                {mask ? (
                    <InputMask
                        mask={mask}
                        value={value}
                        name={name}
                        maskChar={maskChar}
                        formatChars={{
                            '9': '[0-9]',
                            a: '[A-Za-z]',
                            '*': '[A-Za-z0-9]',
                        }}
                        onChange={this.handleChange}
                    >
                        {(inputProps) => (
                            <input
                                {...inputProps}
                                className={
                                    className ||
                                    `w-full p-2 border ${
                                        isValid ? 'border-gray-400' : 'border-red-300'
                                    } rounded focus:border-focused focus:shadow-input focus:outline-none`
                                }
                                type={type ? type : 'text'}
                                value={value}
                                name={name}
                                autoComplete="off"
                                onChange={this.handleChange}
                                placeholder={
                                    typeof placeholder === 'object'
                                        ? placeholder[language as keyof typeof placeholder]
                                        : placeholder
                                }
                                disabled={disabled}
                                autoFocus={autoFocus}
                                onKeyUp={this.handlePress} // Add onKeyPress handler
                                min={type === 'number' ? min : ''}
                            />
                        )}
                    </InputMask>
                ) : (
                    <input
                        className={twclsx(
                            'w-full',
                            'p-2',
                            'border',
                            isValid ? 'border-gray-400' : 'border-red-300',
                            'rounded',
                            'focus:border-focused',
                            'focus:shadow-input',
                            'focus:outline-none',
                            className
                        )}
                        autoComplete="off"
                        type={type ? type : 'text'}
                        value={value}
                        name={name}
                        onChange={this.handleChange}
                        placeholder={
                            typeof placeholder === 'object'
                                ? placeholder[language as keyof typeof placeholder]
                                : placeholder
                        }
                        disabled={disabled}
                        autoFocus={autoFocus}
                        onKeyUp={this.handlePress} // Add onKeyPress handler
                        min={type === 'number' ? min : ''}
                    />
                )}
            </>
        );
    }
}

const inputStateToProps = () => ({});

const inputDispatchToProps = () => ({});

export default connect(inputStateToProps, inputDispatchToProps)(Input);
